import {
  IDisplayedMeasure,
  IParsedChartData,
  ITableSettings,
} from '../interfaces';
import {
  IComparisonConfig,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import {
  calculateMeasureIndex,
  filterDataTableRows,
  generateDataTableRows,
  generateSegments,
  getColumnAggregatedValue,
} from './utils';
import { getCharacterByIncrementedIndex } from '../../../utils/character';
import { assertCondition } from '../../../utils/types';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';

interface IProps {
  chartData: IParsedChartData;
  query: IAnalysisResponse['query'];
  displayedMeasures: IDisplayedMeasure[];
  comparisonConfig: IComparisonConfig | null;
  tableSettings: ITableSettings;
}

interface ICsvData {
  headers: string[];
  data: (string | number | null)[][];
}

const getChartDataTableCsvData = ({
  chartData,
  query,
  displayedMeasures,
  comparisonConfig,
  tableSettings,
}: IProps): ICsvData => {
  assertCondition(chartData.queryType === QueryType.DataTable);
  assertCondition(query?.__typename === 'DataTableQuery');
  const uniqueSegments = generateSegments(
    chartData,
    query?.breakdown?.cohortRefs,
  );
  const breakdowns = query?.breakdown?.groupbys ?? [];

  // use builderInfo measures to generate values
  const measures = query.measures ?? [];

  // use displayed measures to get the measure name, contains the renamed measure
  const measureNames = displayedMeasures.map(
    (measure: IDisplayedMeasure) => measure.name,
  );
  const rows = generateDataTableRows(
    chartData,
    uniqueSegments,
    !!comparisonConfig,
    tableSettings.showPercentDelta,
  );
  const columnLength = rows[0]?.length;

  const numSegments = uniqueSegments.length;
  const numBreakdowns = breakdowns.length;
  const numComparisonColumns = tableSettings.showPercentDelta ? 3 : 2;

  const headers: string[] = Array.from({ length: columnLength }).map(
    (_, index) => {
      if (index < numBreakdowns) {
        return '';
      }
      const colIndex = index - numBreakdowns;
      const measureIndex = calculateMeasureIndex(
        colIndex,
        uniqueSegments,
        !!comparisonConfig,
        numComparisonColumns,
      );

      if (
        numSegments === 0 &&
        (!!comparisonConfig ? colIndex % numComparisonColumns === 0 : true)
      ) {
        const prefix = getCharacterByIncrementedIndex('A', measureIndex);
        return `${prefix}: ${measureNames[measureIndex]}`;
      }

      if (numSegments > 0 && colIndex % numSegments === 0) {
        const prefix = getCharacterByIncrementedIndex('A', measureIndex);
        if (measureNames[measureIndex]) {
          return `${prefix}: ${measureNames[measureIndex]}`;
        }
      }

      return '';
    },
  );

  const data: Array<Array<number | string | null>> = [];

  // if segments exist, push segment content row
  if (numSegments > 0) {
    const segmentRow: string[] = Array.from({ length: columnLength }).map(
      (_, index) => {
        if (index < numBreakdowns) {
          return '';
        }

        const colIndex = index - numBreakdowns;
        // calculate the segment index
        const segmentIndex = colIndex % numSegments;
        return uniqueSegments[segmentIndex];
      },
    );

    data.push(segmentRow);
  }

  // if comparison exists, push comparison header row
  if (!!comparisonConfig) {
    const comparisonRow: string[] = Array.from({ length: columnLength }).map(
      (_, index) => {
        if (index < numBreakdowns) {
          return '';
        }

        const colIndex = index - numBreakdowns;
        if (colIndex % numComparisonColumns === 0) {
          return 'Previous';
        }
        if (colIndex % numComparisonColumns === 1) {
          return 'Current';
        }
        if (colIndex % numComparisonColumns === 2) {
          return 'Percent Delta';
        }

        return '';
      },
    );

    data.push(comparisonRow);
  }

  // if breakdowns exist, push breakdown content row
  if (numBreakdowns > 0) {
    const breakdownRow: (string | number | null)[] = Array.from({
      length: columnLength,
    }).map((_, index) => {
      if (index < numBreakdowns) {
        return breakdowns[index].field.name;
      }
      const measureIndex = calculateMeasureIndex(
        index - numBreakdowns,
        uniqueSegments,
        !!comparisonConfig,
        numComparisonColumns,
      );
      const measure = measures[measureIndex];

      if (measure?.__typename === 'SimpleMeasure' && numBreakdowns > 0) {
        return getColumnAggregatedValue(rows, index, measure.function.key);
      }

      return '';
    });

    data.push(breakdownRow);
  }

  const { searchTerm, sortColumnIndex, sortDirection } = tableSettings;

  const contentRows = filterDataTableRows({
    rows,
    searchTerm,
    sortColumnIndex,
    sortDirection,
  });

  // push value content from the rows.
  contentRows.forEach((row) => {
    data.push(row.map((cell) => cell));
  });

  return {
    headers,
    data,
  };
};

export default getChartDataTableCsvData;
